import {
  TWorkflowDefinition,
  TWorkflowStepDefinition,
  TWorkflowStepTransition,
  TWorkflowTriggerDefinition,
  WorkflowStepType,
  WorkflowTriggerType,
} from '@commenda/types';
import { Edge, MarkerType, Node } from '@xyflow/react';
import { WorkflowHandlerID } from '../../workflow/ui';
import { TRIGGER_NODE_TYPE, TTriggerNodeData } from '../types/node';
import { WORKFLOW_EDITOR_ADAPTER_CONSTANTS } from './constants';

// A boolean gateway exposes only branch handles, and React Flow silently drops
// edges whose handle id isn't real on the node.
const VALID_GATEWAY_SOURCE_HANDLES = new Set<string>([
  WorkflowHandlerID.SOURCE_BOTTOM_TRUE,
  WorkflowHandlerID.SOURCE_BOTTOM_FALSE,
  WorkflowHandlerID.SOURCE_RIGHT_TRUE,
  WorkflowHandlerID.SOURCE_RIGHT_FALSE,
]);

export class DSLToFlowAdapter {
  private static getInitialPosition(index: number) {
    const columns = WORKFLOW_EDITOR_ADAPTER_CONSTANTS.NODE_PALETTE_COLUMNS;
    const xSpacing = WORKFLOW_EDITOR_ADAPTER_CONSTANTS.NODE_PALETTE_X_SPACING;
    const ySpacing = WORKFLOW_EDITOR_ADAPTER_CONSTANTS.NODE_PALETTE_Y_SPACING;
    const offset = WORKFLOW_EDITOR_ADAPTER_CONSTANTS.NODE_PALETTE_OFFSET;
    const col = index % columns;
    const row = Math.floor(index / columns);

    return {
      x: col * xSpacing + offset,
      y: row * ySpacing + offset,
    };
  }

  private static stepToNode(
    step: TWorkflowStepDefinition,
    index: number
  ): Node {
    return {
      id: step.id,
      type: step.type,
      position:
        step.ui?.reactFlow?.position ||
        DSLToFlowAdapter.getInitialPosition(index),
      data: step,
    };
  }

  private static triggerToNode(
    trigger: TWorkflowTriggerDefinition,
    index: number
  ): Node<TTriggerNodeData> {
    const triggerData: TTriggerNodeData = {
      id: trigger.id,
      type: TRIGGER_NODE_TYPE,
      triggerType: trigger.type,
      name: trigger.name,
      eventType:
        trigger.type === WorkflowTriggerType.INTERNAL_EVENT
          ? trigger.eventType
          : undefined,
    };

    return {
      id: trigger.id,
      type: TRIGGER_NODE_TYPE,
      position: trigger.ui?.reactFlow?.position || {
        x: -250,
        y: 50 + index * 120,
      },
      data: triggerData,
    };
  }

  // Gateway edges authored by the AI builder carry a plain source handle the
  // gateway doesn't have, so derive it: conditioned branch TRUE, fallthrough FALSE.
  private static resolveHandles(
    transition: TWorkflowStepTransition,
    sourceType?: WorkflowStepType
  ): { sourceHandle?: string; targetHandle?: string } {
    const stored = transition.ui?.reactFlow?.handle;

    if (sourceType === WorkflowStepType.GATEWAY_BOOLEAN_EXCLUSIVE_SPLIT) {
      const sourceHandle =
        stored?.sourceHandle &&
        VALID_GATEWAY_SOURCE_HANDLES.has(stored.sourceHandle)
          ? stored.sourceHandle
          : transition.condition
            ? WorkflowHandlerID.SOURCE_BOTTOM_TRUE
            : WorkflowHandlerID.SOURCE_BOTTOM_FALSE;
      return {
        sourceHandle,
        targetHandle: stored?.targetHandle ?? WorkflowHandlerID.TARGET_TOP,
      };
    }

    return {
      sourceHandle: stored?.sourceHandle,
      targetHandle: stored?.targetHandle,
    };
  }

  private static transitionToEdge(
    transition: TWorkflowStepTransition,
    sourceType?: WorkflowStepType
  ): Edge {
    const { sourceHandle, targetHandle } = DSLToFlowAdapter.resolveHandles(
      transition,
      sourceType
    );

    return {
      id: transition.id,
      source: transition.fromStepId,
      target: transition.toStepId,
      type: 'smoothstep',
      label: transition.label || (transition.condition ? '?' : ''),
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
      },
      animated: !!transition.condition,
      data: {
        condition: transition.condition,
      },
      sourceHandle,
      targetHandle,
    };
  }

  static dslToFlow(workflow: TWorkflowDefinition): {
    nodes: Node[];
    edges: Edge[];
  } {
    const stepNodes = workflow.steps.map((step, index) =>
      DSLToFlowAdapter.stepToNode(step, index)
    );

    const triggerNodes = (workflow.triggers ?? []).map((trigger, index) =>
      DSLToFlowAdapter.triggerToNode(trigger, index)
    );

    const stepTypeById = new Map<string, WorkflowStepType>(
      workflow.steps.map((step) => [step.id, step.type])
    );

    const edges = workflow.stepTransitions.map((transition) =>
      DSLToFlowAdapter.transitionToEdge(
        transition,
        stepTypeById.get(transition.fromStepId)
      )
    );

    return { nodes: [...triggerNodes, ...stepNodes], edges };
  }
}
